import { MAX_OCCURRENCE_COUNT } from '@liza/fhir-utils';
import { DatePicker } from '@liza/ui/components/atoms/DatePicker';
import { Field, FieldContent, FieldGroup, FieldLabel } from '@liza/ui/components/ui/field';
import { Input } from '@liza/ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@liza/ui/components/ui/select';
import type { ScheduleEndMode, SchedulePickerState, ScheduleType } from '@liza/ui/utils/schedulePicker';
import { useTranslation } from 'react-i18next';

/**
 * "Ends" controls for the recurring modes: never, on a calendar date, or after
 * N occurrences. The days-per-week quota does not offer a count — its "times"
 * are completions, which the schedule engine cannot count structurally — so
 * an inherited `afterCount` displays as never there (and the writer ignores it).
 */
export function ScheduleEndsFields({
  scheduleType,
  endMode,
  endDate,
  endCount,
  onPatch,
}: {
  scheduleType: ScheduleType;
  endMode: ScheduleEndMode;
  endDate: string;
  endCount: number;
  onPatch: (patch: Partial<SchedulePickerState>) => void;
}): React.ReactNode {
  const { t } = useTranslation('common');
  if (scheduleType === 'once' || scheduleType === 'asNeeded') return null;
  const allowCount = scheduleType !== 'daysPerWeek';
  const displayMode = !allowCount && endMode === 'afterCount' ? 'never' : endMode;
  return (
    <FieldGroup>
      <Field>
        <FieldLabel>{t('endsLabel')}</FieldLabel>
        <FieldContent className="flex flex-row items-center gap-1">
          <Select value={displayMode} onValueChange={(v) => onPatch({ endMode: v as ScheduleEndMode })}>
            <SelectTrigger aria-label={t('endsLabel')} className="h-9 w-auto flex-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="never">{t('endsNever')}</SelectItem>
              <SelectItem value="onDate">{t('endsOnDate')}</SelectItem>
              {allowCount && <SelectItem value="afterCount">{t('endsAfterCountOption')}</SelectItem>}
            </SelectContent>
          </Select>
          {displayMode === 'onDate' && (
            <DatePicker
              value={endDate}
              onChange={(value) => onPatch({ endDate: value })}
              aria-label={t('endsOnDate')}
            />
          )}
          {displayMode === 'afterCount' && (
            <>
              <Input
                type="number"
                min={1}
                max={MAX_OCCURRENCE_COUNT}
                value={endCount}
                onChange={(e) =>
                  onPatch({ endCount: Math.min(MAX_OCCURRENCE_COUNT, Math.max(1, parseInt(e.target.value) || 1)) })
                }
                className="w-20"
                aria-label={t('endsAfterCountOption')}
              />
              <span className="text-sm text-muted-foreground">{t('timesUnit')}</span>
            </>
          )}
        </FieldContent>
      </Field>
    </FieldGroup>
  );
}
